import {
  ChevronRight,
  LoginOutlined,
  LogoutOutlined,
  PersonOutlineOutlined,
  StorefrontOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, MouseEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AUTH_CHANGED_EVENT,
  AUTH_LOGIN_REQUESTED_EVENT,
  clearAuthSession,
  loadAuthSession,
  saveAuthSession,
} from "../../../services/authStorage";
import { COLORS } from "../../../values/colors";
import { loginUser, requestPasswordReset, UserDto } from "../../../services/userApi";

interface UserMenuProps {
  compact?: boolean;
}

const UserMenu = ({ compact = false }: UserMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetErrorMessage, setResetErrorMessage] = useState<string | null>(null);
  const [resetSuccessMessage, setResetSuccessMessage] = useState<string | null>(null);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);

  useEffect(() => {
    const syncSession = () => {
      const session = loadAuthSession();
      setCurrentUser(session?.user ?? null);
    };
    const openLoginDialog = () => {
      setErrorMessage(null);
      setDialogOpen(true);
    };

    const syncSessionFromStorage = (event: StorageEvent) => {
      if (event.key && !event.key.startsWith("eckenmoorhexen.auth.")) {
        return;
      }

      syncSession();
    };

    syncSession();
    window.addEventListener(AUTH_CHANGED_EVENT, syncSession);
    window.addEventListener(AUTH_LOGIN_REQUESTED_EVENT, openLoginDialog);
    window.addEventListener("storage", syncSessionFromStorage);

    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, syncSession);
      window.removeEventListener(AUTH_LOGIN_REQUESTED_EVENT, openLoginDialog);
      window.removeEventListener("storage", syncSessionFromStorage);
    };
  }, []);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShopClick = () => {
    handleMenuClose();

    if (location.pathname.startsWith("/shop")) {
      navigate("/shop");
      return;
    }

    window.open("/shop", "_blank", "noopener,noreferrer");
  };

  const handleLoginOpen = () => {
    setErrorMessage(null);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleLoginClose = () => {
    setDialogOpen(false);
    setEmail("");
    setPasswort("");
    setErrorMessage(null);
  };

  const handleResetDialogOpen = () => {
    setResetEmail(email);
    setResetErrorMessage(null);
    setResetSuccessMessage(null);
    setResetDialogOpen(true);
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
    setResetEmail("");
    setResetErrorMessage(null);
    setResetSuccessMessage(null);
  };

  const handleLogout = () => {
    clearAuthSession();
    setCurrentUser(null);
    handleMenuClose();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const response = await loginUser({ email, passwort });
      saveAuthSession(response);
      setCurrentUser(response.user);
      setDialogOpen(false);
      setEmail("");
      setPasswort("");
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Anmeldung fehlgeschlagen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccountClick = () => {
    handleMenuClose();
    navigate("/konto");
  };

  const handleAdminClick = () => {
    handleMenuClose();
    navigate("/admin/mitglieder");
  };

  const handleShopManagerClick = () => {
    handleMenuClose();
    navigate("/shopverwaltung/reservierungen");
  };

  const handleOrdersClick = () => {
    handleMenuClose();
    navigate("/konto/reservierungen");
  };

  const handlePasswordResetRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setResetErrorMessage(null);
    setResetSuccessMessage(null);
    setIsResetSubmitting(true);

    try {
      await requestPasswordReset({ email: resetEmail });
      setResetSuccessMessage(
        "Wenn zu dieser E-Mail-Adresse ein Konto existiert, wurde eine Nachricht zum Zurücksetzen des Passworts versendet."
      );
    } catch (error) {
      setResetErrorMessage(error instanceof Error ? error.message : "Passwort-Reset konnte nicht angefordert werden.");
    } finally {
      setIsResetSubmitting(false);
    }
  };

  const displayName = currentUser
    ? `${currentUser.vorname} ${currentUser.name}`
    : "Benutzerkonto";
  const initials = currentUser
    ? `${currentUser.vorname.charAt(0)}${currentUser.name.charAt(0)}`.toUpperCase()
    : null;
  const triggerGlyph = initials ? (
    <Box
      sx={{
        width: compact ? 34 : 36,
        height: compact ? 34 : 36,
        borderRadius: "50%",
        display: "grid",
        placeItems: "center",
        background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
        color: COLORS.white,
        fontSize: compact ? "0.82rem" : "0.88rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  ) : (
    <PersonOutlineOutlined sx={{ fontSize: compact ? 30 : 24 }} />
  );

  return (
    <>
      {compact ? (
        <IconButton
          onClick={handleMenuOpen}
          sx={{
            color: "#fff",
            marginRight: 0,
          }}
        >
          {triggerGlyph}
        </IconButton>
      ) : (
        <Button
          onClick={handleMenuOpen}
          variant="text"
          startIcon={triggerGlyph}
          sx={{
            color: COLORS.white,
            borderRadius: "999px",
            padding: "0.7rem 1rem",
            border: "1px solid rgba(255,255,255,0.14)",
            background: currentUser
              ? "linear-gradient(135deg, rgba(181,115,76,0.25), rgba(255,255,255,0.06))"
              : "rgba(255,255,255,0.05)",
            textTransform: "none",
            minWidth: "auto",
            maxWidth: "210px",
            overflow: "hidden",
            flexShrink: 0,
            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <Stack spacing={0} alignItems="flex-start" sx={{ lineHeight: 1.1, overflow: "hidden" }}>
            <Typography
              variant="body2"
              noWrap
              sx={{ fontWeight: 700, color: COLORS.white, maxWidth: "100%", fontSize: "0.8rem" }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{ color: "rgba(255,255,255,0.65)", maxWidth: "100%", fontSize: "0.66rem" }}
            >
              {currentUser ? "Angemeldet" : "Login öffnen"}
            </Typography>
          </Stack>
        </Button>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              overflow: "visible",
              marginTop: 1.2,
              borderRadius: "1.4rem",
              color: COLORS.white,
              border: "1px solid rgba(181,115,76,0.25)",
              background:
                "linear-gradient(180deg, rgba(22,22,22,0.98), rgba(12,12,12,0.98))",
              boxShadow: "0 24px 60px rgba(0,0,0,0.45)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: -8,
                right: 18,
                width: 16,
                height: 16,
                background: "rgba(19,19,19,0.98)",
                transform: "rotate(45deg)",
                borderTop: "1px solid rgba(181,115,76,0.25)",
                borderLeft: "1px solid rgba(181,115,76,0.25)",
              },
            },
          },
        }}
      >
        <Box sx={{ padding: "1rem 1rem 0.85rem" }}>
          <Paper
            elevation={0}
            sx={{
              padding: "1rem",
              borderRadius: "1.2rem",
              color: COLORS.white,
              background:
                "radial-gradient(circle at top left, rgba(181,115,76,0.18), transparent 40%), rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "16px",
                  display: "grid",
                  placeItems: "center",
                  background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                }}
              >
                {currentUser
                  ? `${currentUser.vorname.charAt(0)}${currentUser.name.charAt(0)}`.toUpperCase()
                  : "EH"}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>{displayName}</Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  {currentUser?.email ?? "Nur Mitglieder des Vereins können sich anmelden."}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={currentUser ? "Session aktiv" : "Nicht angemeldet"}
              size="small"
              sx={{
                marginTop: 1.25,
                color: COLORS.creamWhite,
                backgroundColor: "rgba(181,115,76,0.16)",
                border: "1px solid rgba(181,115,76,0.3)",
              }}
            />
          </Paper>
        </Box>
        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
        {currentUser ? (
          <Box sx={{ px: 2, pb: 1.1 }}>
            <Button
              onClick={handleAccountClick}
              fullWidth
              variant="outlined"
              startIcon={<PersonOutlineOutlined />}
              endIcon={<ChevronRight />}
              sx={{
                justifyContent: "space-between",
                borderRadius: "999px",
                borderColor: "rgba(255,255,255,0.14)",
                color: COLORS.white,
                textTransform: "none",
                padding: "0.8rem 1rem",
              }}
            >
              Konto ansehen
            </Button>
            <Button
              onClick={handleOrdersClick}
              fullWidth
              variant="outlined"
              startIcon={<StorefrontOutlined />}
              endIcon={<ChevronRight />}
              sx={{
                justifyContent: "space-between",
                borderRadius: "999px",
                borderColor: "rgba(181,115,76,0.24)",
                color: COLORS.white,
                textTransform: "none",
                padding: "0.8rem 1rem",
                marginTop: "0.75rem",
                background: "rgba(181,115,76,0.08)",
              }}
            >
              Meine Reservierungen
            </Button>
            {currentUser.role === "administrator" ? (
              <Button
                onClick={handleAdminClick}
                fullWidth
                variant="outlined"
                startIcon={<PersonOutlineOutlined />}
                endIcon={<ChevronRight />}
                sx={{
                  justifyContent: "space-between",
                  borderRadius: "999px",
                  borderColor: "rgba(155,207,103,0.24)",
                  color: COLORS.white,
                  textTransform: "none",
                  padding: "0.8rem 1rem",
                  marginTop: "0.75rem",
                  background: "rgba(155,207,103,0.08)",
                }}
              >
                Mitgliederverwaltung
              </Button>
            ) : null}
            {currentUser.role === "shopmanager" ? (
              <Button
                onClick={handleShopManagerClick}
                fullWidth
                variant="outlined"
                startIcon={<StorefrontOutlined />}
                endIcon={<ChevronRight />}
                sx={{
                  justifyContent: "space-between",
                  borderRadius: "999px",
                  borderColor: "rgba(181,115,76,0.24)",
                  color: COLORS.white,
                  textTransform: "none",
                  padding: "0.8rem 1rem",
                  marginTop: "0.75rem",
                  background: "rgba(181,115,76,0.08)",
                }}
              >
                Reservierungen verwalten
              </Button>
            ) : null}
          </Box>
        ) : null}
        {currentUser ? (
          <MenuItem onClick={handleLogout} sx={{ py: 1.2, gap: 1.2 }}>
            <LogoutOutlined fontSize="small" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Abmelden</Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
                Lokale Sitzung beenden
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleLoginOpen} sx={{ py: 1.2, gap: 1.2 }}>
            <LoginOutlined fontSize="small" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 600 }}>Anmelden</Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.55)" }}>
                Mit E-Mail und Passwort einloggen
              </Typography>
            </Box>
            <ChevronRight fontSize="small" />
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={handleLoginClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "1.8rem",
            color: COLORS.white,
            background:
              "radial-gradient(circle at top left, rgba(181,115,76,0.18), transparent 32%), linear-gradient(180deg, rgba(20,20,20,0.98), rgba(10,10,10,0.98))",
            border: "1px solid rgba(181,115,76,0.28)",
            boxShadow: "0 28px 70px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Typography variant="overline" sx={{ color: COLORS.apronBrown, letterSpacing: "0.18em" }}>
            Benutzerkonto
          </Typography>
          <Typography variant="h4" sx={{ color: COLORS.white, marginTop: 0.5, fontWeight: 700 }}>
            Anmelden
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack component="form" spacing={2} sx={{ pt: 2 }} onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>
              Melde dich mit einem bestehenden Konto an. Für den aktuellen Zugang ist bereits
              ein Benutzerkonto hinterlegt.
            </Typography>
            <TextField
              label="E-Mail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
              InputProps={{
                sx: {
                  color: COLORS.white,
                  borderRadius: "1rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.15)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(181,115,76,0.4)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.apronBrown,
                  },
                },
              }}
            />
            <TextField
              label="Passwort"
              type="password"
              value={passwort}
              onChange={(event) => setPasswort(event.target.value)}
              required
              fullWidth
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
              InputProps={{
                sx: {
                  color: COLORS.white,
                  borderRadius: "1rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.15)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(181,115,76,0.4)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.apronBrown,
                  },
                },
              }}
            />
            {errorMessage && (
              <Typography variant="body2" color="error" sx={{ color: "#ff9f9f" }}>
                {errorMessage}
              </Typography>
            )}
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={handleResetDialogOpen}
              sx={{ color: COLORS.creamWhite, alignSelf: "flex-start" }}
            >
              Passwort vergessen?
            </Link>
            <DialogActions sx={{ px: 0 }}>
              <Button
                onClick={handleLoginClose}
                variant="outlined"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  borderRadius: "999px",
                  borderColor: "rgba(255,255,255,0.18)",
                  textTransform: "none",
                  paddingInline: "1.2rem",
                }}
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  borderRadius: "999px",
                  paddingInline: "1.4rem",
                  background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                  textTransform: "none",
                }}
              >
                {isSubmitting ? "Pruefe..." : "Anmelden"}
              </Button>
            </DialogActions>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resetDialogOpen}
        onClose={handleResetDialogClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "1.8rem",
            color: COLORS.white,
            background:
              "radial-gradient(circle at top left, rgba(181,115,76,0.18), transparent 32%), linear-gradient(180deg, rgba(20,20,20,0.98), rgba(10,10,10,0.98))",
            border: "1px solid rgba(181,115,76,0.28)",
            boxShadow: "0 28px 70px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Typography variant="overline" sx={{ color: COLORS.apronBrown, letterSpacing: "0.18em" }}>
            Benutzerkonto
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.white, marginTop: 0.5, fontWeight: 700 }}>
            Passwort vergessen
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack component="form" spacing={2} sx={{ pt: 2 }} onSubmit={handlePasswordResetRequest}>
            <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.7 }}>
              Gib die E-Mail-Adresse deines Kontos ein. Wenn ein Benutzer dazu existiert, senden wir dir
              einen Link zum Zurücksetzen deines Passworts.
            </Typography>
            <TextField
              label="E-Mail"
              type="email"
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              required
              fullWidth
              InputLabelProps={{ sx: { color: "rgba(255,255,255,0.55)" } }}
              InputProps={{
                sx: {
                  color: COLORS.white,
                  borderRadius: "1rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.15)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(181,115,76,0.4)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: COLORS.apronBrown,
                  },
                },
              }}
            />
            {resetErrorMessage ? (
              <Typography variant="body2" sx={{ color: "#ff9f9f" }}>
                {resetErrorMessage}
              </Typography>
            ) : null}
            {resetSuccessMessage ? (
              <Typography variant="body2" sx={{ color: COLORS.creamWhite }}>
                {resetSuccessMessage}
              </Typography>
            ) : null}
            <DialogActions sx={{ px: 0 }}>
              <Button
                onClick={handleResetDialogClose}
                variant="outlined"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  borderRadius: "999px",
                  borderColor: "rgba(255,255,255,0.18)",
                  textTransform: "none",
                  paddingInline: "1.2rem",
                }}
              >
                Schließen
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isResetSubmitting}
                sx={{
                  borderRadius: "999px",
                  paddingInline: "1.4rem",
                  background: `linear-gradient(135deg, ${COLORS.apronBrown}, ${COLORS.apronBrownDark})`,
                  textTransform: "none",
                }}
              >
                {isResetSubmitting ? "Sende..." : "Reset-Link senden"}
              </Button>
            </DialogActions>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserMenu;

